import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import {useState} from "react";
import MakeItLiveCta from "../components/MakeItLiveCta.tsx";
import {GeneratorProgress, GeneratorTopCta, useStagedGenerate} from "../components/generatorReveal.tsx";
import DuprPartnerBadge from "../components/DuprPartnerBadge.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import TournamentManagementCta from "../landing/sections/TournamentManagementCta.tsx";

export const Route = createFileRoute('/double-elimination-bracket-generator')({
    component: RouteComponent,
})

type Slot = {name: string; muted: boolean};
type Match = {teams: [Slot, Slot]};
type BracketRound = {title: string; matches: Match[]};
type DoubleElim = {winners: BracketRound[]; losers: BracketRound[]; finals: BracketRound[]};

function nextPowerOfTwo(n: number): number {
    return Math.pow(2, Math.ceil(Math.log2(n)));
}

function winnersRoundName(size: number): string {
    if (size === 2) return "Winners Final";
    if (size === 4) return "Winners Semi-finals";
    if (size === 8) return "Winners Quarter-finals";
    return `Winners Round of ${size}`;
}

const realSlot = (name: string): Slot => ({name, muted: name === "BYE"});
const ph = (name: string): Slot => ({name, muted: true});

function buildDoubleElim(entrants: string[]): DoubleElim {
    const bracketSize = nextPowerOfTwo(entrants.length);
    const players = [...entrants];
    while (players.length < bracketSize) players.push("BYE");

    const winnersRounds = Math.log2(bracketSize);

    // Winners bracket
    const winners: BracketRound[] = [];
    let currentSize = bracketSize;
    for (let round = 0; round < winnersRounds; round++) {
        const matches: Match[] = [];
        const matchesInRound = currentSize / 2;
        for (let m = 0; m < matchesInRound; m++) {
            if (round === 0) {
                matches.push({teams: [realSlot(players[m * 2]), realSlot(players[m * 2 + 1])]});
            } else {
                matches.push({teams: [ph("Winner of WB previous round"), ph("Winner of WB previous round")]});
            }
        }
        winners.push({title: winnersRoundName(currentSize), matches});
        currentSize /= 2;
    }

    // Losers bracket
    const losers: BracketRound[] = [];
    const losersRoundCount = Math.max(1, winnersRounds * 2 - 2);
    let loserMatchCount = Math.max(1, bracketSize / 4);
    for (let round = 1; round <= losersRoundCount; round++) {
        const count = Math.max(1, Math.ceil(loserMatchCount));
        const matches: Match[] = Array.from({length: count}, () => ({
            teams: [ph("Loser / Winner feeds in"), ph("Loser / Winner feeds in")] as [Slot, Slot],
        }));
        losers.push({title: `Losers Round ${round}`, matches});
        if (round % 2 === 0 && loserMatchCount > 1) loserMatchCount /= 2;
    }

    // Grand final + reset
    const finals: BracketRound[] = [
        {title: "Grand Final", matches: [{teams: [ph("Winner of Winners Bracket"), ph("Winner of Losers Bracket")]}]},
        {title: "Bracket Reset If Needed", matches: [{teams: [ph("Grand Final Winner"), ph("Grand Final Loser")]}]},
    ];

    return {winners, losers, finals};
}

const STEPS = [
    {title: "Start in Winners Bracket", description: "All participants begin in the main winners bracket."},
    {title: "First Loss Drops Down", description: "After one loss, players move into the losers bracket."},
    {title: "Second Loss Eliminates", description: "Participants are eliminated only after losing twice."},
];

const CALC_TABLE = [
    {n: 4, min: 6, max: 7, use: "Small competitive event"},
    {n: 8, min: 14, max: 15, use: "Club tournament"},
    {n: 16, min: 30, max: 31, use: "Championship bracket"},
    {n: 32, min: 62, max: 63, use: "Large competition"},
    {n: 64, min: 126, max: 127, use: "Major tournament"},
];

const BEST_FOR = ["Competitive tournaments", "Championship events", "Events where players deserve a second chance", "Esports and bracket-based competitions", "Padel, tennis and pickleball knockout events"];
const LESS_IDEAL = ["Very large one-day events", "Social club nights", "Events with limited court time", "Leagues where every player should face everyone", "Beginner events where simplicity matters most"];

const VS_ROWS = [
    {feature: "Eliminated after", de: "Two losses", se: "One loss"},
    {feature: "Second chance", de: "Yes", se: "No"},
    {feature: "Match volume", de: "Medium to high", se: "Low"},
    {feature: "Bracket complexity", de: "Higher", se: "Lower"},
    {feature: "Best for speed", de: "No", se: "Yes"},
    {feature: "Best for fairness", de: "Yes", se: "No"},
];

const FAQS = [
    {
        q: 'What is a double elimination bracket?',
        a: <>A double elimination bracket is a tournament structure where every participant must lose twice before being eliminated.</>,
    },
    {
        q: 'How many matches are in double elimination?',
        a: <>Double elimination usually has between 2n − 2 and 2n − 1 matches, depending on whether a bracket reset is needed in the grand final.</>,
    },
    {
        q: 'What is the losers bracket?',
        a: <>The losers bracket is where participants go after their first loss. If they lose again, they are eliminated.</>,
    },
    {
        q: 'Is double elimination better than round robin?',
        a: <>Double elimination is faster than <Link to={"/round-robin-tournament-generator/" as any} className="text-[#082F8A] underline font-semibold">round robin</Link> but less comprehensive. Round robin is usually better for rankings, while double elimination is better for competitive knockout events.</>,
    },
    {
        q: 'Can ScorePal manage a full double elimination tournament?',
        a: <>Yes. ScorePal manages fixtures, live scores, brackets, standings, rankings and tournament communication from one platform.</>,
    },
];

function BracketColumns({rounds}: {rounds: BracketRound[]}) {
    return (
        <div className="flex gap-6 overflow-x-auto pb-4">
            {rounds.map((round, ri) => (
                <div key={ri} className="min-w-[230px] flex flex-col gap-4">
                    <div className="font-bebas text-lg text-[#1a1a2e] font-bold">{round.title}</div>
                    {round.matches.map((match, mi) => (
                        <div key={mi} className="rounded-2xl border border-[#e5e7eb] overflow-hidden bg-white">
                            {match.teams.map((t, ti) => (
                                <div
                                    key={ti}
                                    className={`px-4 py-3 font-inter text-sm bg-[#F8FAFC] ${ti === 0 ? "border-b border-[#e5e7eb]" : ""} ${t.muted ? "text-[#1a1a2e]/40 italic" : "text-[#1a1a2e]"}`}
                                >
                                    {t.name}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function RouteComponent() {
    const [playersText, setPlayersText] = useState("");
    const [result, setResult] = useState<DoubleElim | null>(null);
    const [summary, setSummary] = useState<{participants: number; winnersRounds: number; min: number; max: number; byes: number} | null>(null);
    const [error, setError] = useState<string | null>(null);

    const gen = useStagedGenerate(["Seeding the winners bracket", "Wiring the losers bracket", "Setting up the grand final"]);

    // Same handoff the results panel uses — hoisted so the top CTA and MakeItLiveCta can't drift.
    const buildPayload = () => {
        const teams = playersText.split("\n").map((n) => n.trim()).filter(Boolean);
        return teams.length >= 2 ? {source: "double-elimination-bracket-generator", format: "double_elim" as const, sport: "padel", teams} : null;
    };

    const generate = () => {
        const entrants = playersText.split("\n").map((n) => n.trim()).filter(Boolean);
        if (entrants.length < 2) {
            setError("Please enter at least two players or teams.");
            setResult(null);
            setSummary(null);
            return;
        }
        const bracketSize = nextPowerOfTwo(entrants.length);
        setError(null);
        setResult(null);
        setSummary(null);
        gen.run(() => {
            setResult(buildDoubleElim(entrants));
            setSummary({
                participants: entrants.length,
                winnersRounds: Math.log2(bracketSize),
                min: entrants.length * 2 - 2,
                max: entrants.length * 2 - 1,
                byes: bracketSize - entrants.length,
            });
        });
    };

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Double Elimination Bracket Generator (Free) | ScorePal</title>
            <meta name="description" content="Create a double elimination tournament bracket instantly. Generate winners and losers bracket fixtures for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby, esports and more with ScorePal's free bracket generator." />
            <link rel="canonical" href={`${config.publicUrl}/double-elimination-bracket-generator/`} />
            <meta property="og:title" content="Double Elimination Bracket Generator | ScorePal" />
            <meta property="og:description" content="Generate a free double elimination bracket with winners and losers brackets for any sport or competition." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/double-elimination-bracket-generator/`} />
            <meta property="og:image" content="https://scorepal.co/images/double-elimination-bracket-generator-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Double Elimination Bracket Generator | ScorePal" />
            <meta name="twitter:description" content="Create double elimination tournament brackets instantly with ScorePal." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "Double Elimination Bracket Generator",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "A free online tool to generate double elimination tournament brackets with winners and losers brackets.",
                "url": "https://scorepal.co/double-elimination-bracket-generator/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
                "offers": {"@type": "Offer", "price": "0", "priceCurrency": "USD"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is a double elimination tournament?", "acceptedAnswer": {"@type": "Answer", "text": "A double elimination tournament is a bracket format where participants are eliminated after two losses instead of one."}},
                    {"@type": "Question", "name": "How does a double elimination bracket work?", "acceptedAnswer": {"@type": "Answer", "text": "Participants start in the winners bracket. After their first loss, they move to the losers bracket. A second loss eliminates them."}},
                    {"@type": "Question", "name": "Is double elimination better than single elimination?", "acceptedAnswer": {"@type": "Answer", "text": "Double elimination is usually fairer than single elimination because players get a second chance, but it takes longer to complete."}},
                    {"@type": "Question", "name": "What is the losers bracket?", "acceptedAnswer": {"@type": "Answer", "text": "The losers bracket is where participants go after their first loss. If they lose again, they are eliminated."}},
                    {"@type": "Question", "name": "Can ScorePal manage a full double elimination tournament?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal manages fixtures, live scores, brackets, standings, rankings and tournament communication."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Double Elimination Bracket Generator", item: "https://scorepal.co/double-elimination-bracket-generator/"}]} />

            {/* Hero + generator */}
            <section className="relative overflow-hidden p-4">
                <DuprPartnerBadge />
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Free Bracket Generator
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    Double Elimination Bracket Generator
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    Create a double elimination tournament bracket instantly. Generate winners bracket, losers
                                    bracket and final match structure for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby, esports and more.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Create Tournament</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Compare Formats
                                    </Link>
                                </div>
                            </div>

                            {/* Generator card */}
                            <div id="generator" className="bg-white rounded-3xl p-6 lg:p-8 shadow-xl scroll-mt-24">
                                <h2 className="font-bebas text-[#1a1a2e] uppercase text-3xl font-bold tracking-wide mb-1">Create Your Bracket</h2>
                                <p className="font-inter text-[#1a1a2e]/60 text-sm mb-5">Enter your players or teams and generate a double elimination bracket.</p>

                                <label htmlFor="players" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Players or teams</label>
                                <textarea
                                    id="players"
                                    value={playersText}
                                    onChange={(e) => setPlayersText(e.target.value)}
                                    placeholder={"Enter one player or team per line\nPlayer 1\nPlayer 2\nPlayer 3\nPlayer 4"}
                                    className="w-full min-h-[150px] resize-y rounded-xl border border-[#e5e7eb] p-4 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <button
                                    type="button"
                                    onClick={generate}
                                    disabled={gen.running}
                                    className="mt-5 w-full rounded-xl bg-[#082F8A] py-3.5 font-inter font-bold text-white transition hover:bg-[#06246b] disabled:opacity-70"
                                >
                                    {gen.running ? "Generating…" : "Generate Double Elimination Bracket"}
                                </button>

                                <GeneratorProgress gen={gen} />

                                {error && <p className="mt-3 font-inter text-sm text-red-600">{error}</p>}
                                {summary && (
                                    <p className="mt-4 font-inter text-sm text-[#1a1a2e]/70">
                                        <strong>{summary.participants}</strong> participants ·{" "}
                                        <strong>{summary.winnersRounds}</strong> winners rounds ·{" "}
                                        <strong>{summary.min}-{summary.max}</strong> matches ·{" "}
                                        <strong>{summary.byes}</strong> byes
                                    </p>
                                )}

                                {result && <GeneratorTopCta buildPayload={buildPayload} />}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Results */}
            {result && (
                <section className="py-12 lg:py-16 bg-white">
                    <div className="container mx-auto px-6 lg:px-12 space-y-8">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-2">
                                Your Double Elimination Bracket
                            </h2>
                            <p className="font-inter text-[#1a1a2e]/70">Players drop to the losers bracket after one loss and are eliminated after a second.</p>
                        </div>

                        <div>
                            <span className="inline-flex rounded-full bg-[#ecfdf3] text-[#12b76a] px-3 py-1.5 font-bebas text-base font-bold mb-3">Winners Bracket</span>
                            <BracketColumns rounds={result.winners} />
                        </div>
                        <div>
                            <span className="inline-flex rounded-full bg-[#fff7ed] text-[#f79009] px-3 py-1.5 font-bebas text-base font-bold mb-3">Losers Bracket</span>
                            <BracketColumns rounds={result.losers} />
                        </div>
                        <div>
                            <span className="inline-flex rounded-full bg-[#eef4ff] text-[#082F8A] px-3 py-1.5 font-bebas text-base font-bold mb-3">Grand Final</span>
                            <BracketColumns rounds={result.finals} />
                        </div>

                        <MakeItLiveCta formatLabel="double-elimination" buildPayload={buildPayload} />
                    </div>
                </section>
            )}

            {/* What is double elimination / steps */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        What Is a Double Elimination Tournament?
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed max-w-4xl">
                        A double elimination tournament is a bracket format where players or teams are eliminated only after
                        two losses. Everyone starts in the winners bracket. After a first loss, they move into the losers
                        bracket. A second loss removes them from the competition.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                        {STEPS.map((step, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{index + 1}</div>
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{step.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Match calculator */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        Double Elimination Match Calculator
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">A double elimination tournament usually requires approximately:</p>

                    <div className="my-6 inline-block rounded-2xl bg-[#EDEEF4] px-8 py-5 font-bebas text-3xl font-bold text-[#082F8A] tracking-wide">
                        2n − 2 or 2n − 1
                    </div>
                    <p className="font-inter text-[#1a1a2e]/80 text-base mb-2">
                        Where <strong>n</strong> is the number of participants. The extra match may be needed if the winners
                        bracket finalist loses the first grand final.
                    </p>

                    <div className="overflow-x-auto mt-6">
                        <div className="min-w-[640px] border border-[#d9dce7] rounded-2xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-[0.7fr_0.8fr_0.8fr_1.5fr] bg-[#F6F7FB] font-bebas text-lg text-[#1a1a2e]">
                                <div className="p-4 border-r border-[#d9dce7]">Participants</div>
                                <div className="p-4 border-r border-[#d9dce7]">Min Matches</div>
                                <div className="p-4 border-r border-[#d9dce7]">Max Matches</div>
                                <div className="p-4">Typical Use</div>
                            </div>
                            {CALC_TABLE.map((row, i) => (
                                <div key={row.n} className={`grid grid-cols-[0.7fr_0.8fr_0.8fr_1.5fr] border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-4 border-r border-[#d9dce7]">{row.n}</div>
                                    <div className="p-4 border-r border-[#d9dce7] font-medium">{row.min}</div>
                                    <div className="p-4 border-r border-[#d9dce7] font-medium">{row.max}</div>
                                    <div className="p-4 text-[#1a1a2e]/80">{row.use}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* When to use */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                        When Should You Use Double Elimination?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#12b76a] mb-3">Best For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {BEST_FOR.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#d92d20] mb-3">Less Ideal For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {LESS_IDEAL.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* DE vs SE */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Double Elimination vs Single Elimination
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Double elimination gives participants a second chance, while <Link to={"/single-elimination-bracket-generator/" as any} className="text-[#082F8A] underline font-semibold">single elimination</Link> is faster and simpler.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[640px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Feature</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#082F8A]">Double Elimination</div>
                                <div className="p-5 text-[#1a1a2e]">Single Elimination</div>
                            </div>
                            {VS_ROWS.map((row, i) => (
                                <div key={row.feature} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/80">{row.feature}</div>
                                    <div className="p-5 border-r border-[#d9dce7] font-medium">{row.de}</div>
                                    <div className="p-5 font-medium">{row.se}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run Your Double Elimination Tournament With ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Generate brackets, manage registrations, track winners and losers, publish live scores and run your tournament from one platform.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Double Elimination", "Single Elimination", "Round Robin", "Live Scores", "Player Rankings"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Double Elimination FAQs"} faqs={FAQS} />
            <TournamentManagementCta />
            <FooterSection />
        </div>
    );
}
